import React, { useState, useEffect } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { confirmDialog } from "../ui/confirm-dialog";

const InventoryConsumables = ({ axiosInstance, selectedWarehouse, warehouses = [] }) => {
    // State for Stock Consumable Management
    const [consumables, setConsumables] = useState([]);
    const [selectedConsumable, setSelectedConsumable] = useState(null);
    const [isEditConsumableOpen, setIsEditConsumableOpen] = useState(false);

    // State for Brand Management
    const [brands, setBrands] = useState([]);

    // State for Consumable Details Modal (Aggregation)
    const [isConsumableDetailsOpen, setIsConsumableDetailsOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [modelConsumables, setModelConsumables] = useState([]);
    
    // User info
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

    // Filtering & Sorting State
    const [brandFilter, setBrandFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [sortOption, setSortOption] = useState("alphabetical");

    // Fetch data on component mount
    useEffect(() => {
        if (axiosInstance) {
            fetchConsumables();
            fetchBrands();
        }
    }, [axiosInstance]);

    const fetchConsumables = async () => {
        try {
            const response = await axiosInstance.get('/stock/consumables');
            setConsumables(response.data);
        } catch (error) {
            console.error("Error fetching consumables:", error);
            toast.error("Failed to fetch consumables");
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await axiosInstance.get('/stock/consumables/brands');
            setBrands(response.data);
        } catch (error) {
            console.error("Error fetching brands:", error);
            toast.error("Failed to fetch brands");
        }
    };

    const handleRowClick = (aggregatedConsumable) => {
        if (!isSuperAdmin) return;
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const relatedConsumables = filteredConsumables.filter(c => 
            normalize(c.model) === normalize(aggregatedConsumable.model) && 
            normalize(c.brand) === normalize(aggregatedConsumable.brand) &&
            normalize(c.itemDescription) === normalize(aggregatedConsumable.itemDescription) &&
            normalize(c.itemCode) === normalize(aggregatedConsumable.itemCode) &&
            (c.warehouse_id || 'null') === (aggregatedConsumable.warehouse_id || 'null')
        );
        setSelectedModel(aggregatedConsumable);
        setModelConsumables(relatedConsumables);
        setIsConsumableDetailsOpen(true);
    };

    const handleEditConsumable = (consumable) => {
        setSelectedConsumable(consumable);
        setIsEditConsumableOpen(true);
    };

    const handleDeleteConsumable = async (consumableId) => {
        const confirmed = await confirmDialog({
            title: "Delete Consumable",
            description: "Are you sure you want to delete this consumable?",
            variant: "destructive"
        });
        if (!confirmed) return;

        try {
            await axiosInstance.delete(`/stock/consumables/${consumableId}`);
            toast.success("Consumable deleted successfully");
            
            // Refresh main list
            await fetchConsumables();
            
            // Update modal list
            setModelConsumables(prev => prev.filter(c => c.id !== consumableId));
            
            // If no consumables left for this model, close modal
            if (modelConsumables.length <= 1) {
                setIsConsumableDetailsOpen(false);
            }
        } catch (error) {
            console.error("Error deleting consumable:", error);
            toast.error("Failed to delete consumable");
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedConsumable(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateConsumable = async () => {
        if (!selectedConsumable) return;
        
        try {
            const payload = {
                brand: selectedConsumable.brand,
                model: selectedConsumable.model,
                itemDescription: selectedConsumable.itemDescription,
                itemCode: selectedConsumable.itemCode,
                quantity: parseInt(selectedConsumable.quantity) || 0,
                warehouse_id: selectedConsumable.warehouse_id || null
            };

            await axiosInstance.put(`/stock/consumables/${selectedConsumable.id}`, payload);
            toast.success("Consumable updated successfully");
            setIsEditConsumableOpen(false);
            setSelectedConsumable(null);
            
            // Refresh main list
            await fetchConsumables();
            
            // Update local state for details modal if it's open
            if (isConsumableDetailsOpen && selectedModel) {
                const response = await axiosInstance.get('/stock/consumables');
                const allConsumables = response.data;
                const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
                const updatedModelConsumables = allConsumables.filter(c => 
                    normalize(c.model) === normalize(selectedModel.model) && 
                    normalize(c.brand) === normalize(selectedModel.brand) &&
                    normalize(c.itemDescription) === normalize(selectedModel.itemDescription) &&
                    normalize(c.itemCode) === normalize(selectedModel.itemCode) &&
                    (c.warehouse_id || 'null') === (selectedModel.warehouse_id || 'null')
                );
                setModelConsumables(updatedModelConsumables);
            }
        } catch (error) {
            console.error("Error updating consumable:", error);
            toast.error("Failed to update consumable");
        }
    };

    // Filter and Sort Consumables
    const filteredConsumables = consumables.filter(consumable => {
        const matchesBrand = brandFilter && brandFilter !== "all" ? consumable.brand === brandFilter : true;
        const searchLower = searchFilter.toLowerCase();
        const matchesSearch = !searchFilter || 
            (consumable.itemCode?.toLowerCase().includes(searchLower) || 
             consumable.itemDescription?.toLowerCase().includes(searchLower) ||
             consumable.model?.toLowerCase().includes(searchLower));
        
        // Warehouse Filter
        const matchesWarehouse = !selectedWarehouse || consumable.warehouse_id === selectedWarehouse.id;

        return matchesBrand && matchesSearch && matchesWarehouse;
    });

    // Aggregate Consumables by Brand + Model + Description + ItemCode + Warehouse
    const aggregatedConsumables = Object.values(filteredConsumables.reduce((acc, consumable) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const key = `${normalize(consumable.brand)}-${normalize(consumable.model)}-${normalize(consumable.itemDescription)}-${normalize(consumable.itemCode)}-${consumable.warehouse_id || 'null'}`;
        
        if (!acc[key]) {
            acc[key] = {
                ...consumable,
                totalQuantity: 0,
                count: 0
            };
        }
        acc[key].totalQuantity += (consumable.quantity || 0);
        acc[key].count += 1;
        return acc;
    }, {}));

    const sortedAggregatedConsumables = aggregatedConsumables.sort((a, b) => {
        switch (sortOption) {
            case "largest_qty":
                return b.totalQuantity - a.totalQuantity;
            case "lowest_qty":
                return a.totalQuantity - b.totalQuantity;
            case "alphabetical":
            default:
                return a.brand.localeCompare(b.brand) || a.model.localeCompare(b.model);
        }
    });

    const totalQuantity = sortedAggregatedConsumables.reduce((sum, consumable) => sum + consumable.totalQuantity, 0);

    const getWarehouseName = (id) => {
        if (!id) return "Unassigned";
        const wh = warehouses.find(w => w.id === id);
        return wh ? wh.name : "Unknown";
    };

    return (
        <div className="h-full flex flex-col space-y-4">
            {/* Top Controls Bar */}
            <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-end">
                <div className="w-[200px] space-y-2">
                    <Label>Brand</Label>
                    <Select value={brandFilter} onValueChange={setBrandFilter}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select Brand" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Brands</SelectItem>
                            {brands.map(brand => (
                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex-1 space-y-2 min-w-[200px]">
                    <Label>Search</Label>
                    <div className="flex gap-2">
                        <Input
                            placeholder="Search Model, Description, or Item Code..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className="flex-1"
                        />
                    </div>
                </div>

                <div className="w-[200px] space-y-2">
                    <Label>Sort By</Label>
                    <Select value={sortOption} onValueChange={setSortOption}>
                        <SelectTrigger>
                            <SelectValue placeholder="Sort By" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                            <SelectItem value="largest_qty">Largest Quantity</SelectItem>
                            <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Stock List Table */}
            <div className="bg-white p-4 rounded-lg shadow flex-1 overflow-hidden flex flex-col">
                <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-600 px-3 py-1.5 rounded-md border border-gray-200">
                        Total: <span className="text-orange-600 ml-1">{sortedAggregatedConsumables.length}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-600 px-3 py-1.5 rounded-md border border-gray-200">
                        Total: <span className="text-black font-bold ml-1">{totalQuantity}</span>
                    </div>
                </div>
                <div className="border rounded-md flex-1 overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Warehouse</TableHead>
                                <TableHead className="font-bold">Brand</TableHead>
                                <TableHead>Model</TableHead>
                                <TableHead>Item Description</TableHead>
                                <TableHead>Item Code</TableHead>
                                <TableHead>Total Quantity</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedAggregatedConsumables.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-4 text-gray-500">
                                        No consumables found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedAggregatedConsumables.map((consumable, index) => (
                                    <TableRow 
                                        key={`${consumable.brand}-${consumable.model}-${consumable.warehouse_id}-${index}`} 
                                        className="hover:bg-green-100 cursor-pointer transition-colors"
                                        onClick={() => handleRowClick(consumable)}
                                    >
                                        <TableCell className="text-gray-600">{getWarehouseName(consumable.warehouse_id)}</TableCell>
                                        <TableCell className="font-bold">{consumable.brand}</TableCell>
                                        <TableCell>{consumable.model}</TableCell>
                                        <TableCell>{consumable.itemDescription}</TableCell>
                                        <TableCell>{consumable.itemCode}</TableCell>
                                        <TableCell>{consumable.totalQuantity}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Consumable Details Modal */}
            <Dialog open={isConsumableDetailsOpen} onOpenChange={setIsConsumableDetailsOpen}>
                <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedModel ? `${selectedModel.brand} ${selectedModel.model}` : 'Consumable Details'}
                        </DialogTitle>
                        <DialogDescription>
                            Entries: {modelConsumables.length}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto mt-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {isSuperAdmin && <TableHead>Added By</TableHead>}
                                    {isSuperAdmin && <TableHead>Date Created</TableHead>}
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Quantity</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {modelConsumables.map((consumable) => (
                                    <TableRow key={consumable.id}>
                                        {isSuperAdmin && <TableCell>{consumable.addedBy || '-'}</TableCell>}
                                        {isSuperAdmin && <TableCell>{new Date(consumable.createdAt).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}</TableCell>}
                                        <TableCell>{consumable.brand}</TableCell>
                                        <TableCell>{consumable.model}</TableCell>
                                        <TableCell>{consumable.itemDescription}</TableCell>
                                        <TableCell>{consumable.itemCode}</TableCell>
                                        <TableCell>{consumable.quantity}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleEditConsumable(consumable)}
                                                >
                                                    <Pencil className="h-4 w-4 text-blue-500" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleDeleteConsumable(consumable.id)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsConsumableDetailsOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Consumable Modal */}
            <Dialog open={isEditConsumableOpen} onOpenChange={setIsEditConsumableOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Edit Consumable</DialogTitle>
                        <DialogDescription>
                            Update the details of the consumable stock.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedConsumable && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-brand" className="text-right">
                                    Brand
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedConsumable.brand} 
                                        onValueChange={(value) => setSelectedConsumable(prev => ({ ...prev, brand: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Brand" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brands.map(brand => (
                                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-warehouse" className="text-right">
                                    Warehouse
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedConsumable.warehouse_id || "unassigned"} 
                                        onValueChange={(value) => setSelectedConsumable(prev => ({ ...prev, warehouse_id: value === "unassigned" ? null : value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Warehouse" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="unassigned">Unassigned</SelectItem>
                                            {warehouses.map(warehouse => (
                                                <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-model" className="text-right">
                                    Model
                                </Label>
                                <Input
                                    id="edit-model"
                                    name="model"
                                    value={selectedConsumable.model}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemDescription" className="text-right">
                                    Description
                                </Label>
                                <Input
                                    id="edit-itemDescription"
                                    name="itemDescription"
                                    value={selectedConsumable.itemDescription}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemCode" className="text-right">
                                    Item Code
                                </Label>
                                <Input
                                    id="edit-itemCode"
                                    name="itemCode"
                                    value={selectedConsumable.itemCode}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-quantity" className="text-right">
                                    Quantity
                                </Label>
                                <Input
                                    id="edit-quantity"
                                    name="quantity"
                                    type="number"
                                    value={selectedConsumable.quantity}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" onClick={handleUpdateConsumable}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default InventoryConsumables;
